import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react"
import { Aperture, ArrowUp, Box, Cylinder, Globe } from "lucide-react"
import { AssetRegenerateOverlayButton, AssetVersionsBadge } from "@/components/media/asset-media-overlay"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { captureControlButtonClass } from "@/components/locations/capture-controls"
import { FovControl } from "@/components/locations/fov-control"
import { MoveKeys, type MoveKeyCode } from "@/components/locations/move-keys"
import { SceneControlIcon } from "@/components/locations/scene-control-icon"
import { TOOLBAR_ROW, ToolbarSliderButton } from "@/components/locations/location-scene-3d-toolbar"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { EditToolbarButton } from "@/components/ui/edit-toolbar-button"
import { useTranslation } from "@/hooks/use-translation"
import {
  createRoomViewerEngine,
  WALL_IDS,
  type RoomShape,
  type RoomViewerCameraState,
  type RoomViewerEngine,
  type WallId,
  type WallScreenAnchor,
} from "@/lib/location-room-viewer/engine"
import { fovMenuSize } from "@/lib/location-scene-3d/fov-control-math"
import { ViewpointThumbGrid } from "@/components/locations/viewpoint-thumb-grid"
import type { LocationCardinalFacing, LocationViewpointRow } from "@/lib/locations-api"
import { cn } from "@/lib/utils"
import "@/components/locations/scene-controls.css"

export type RoomViewerActions = {
  createViewpoint: () => void
  applyViewpoint: (cameraMeta: RoomViewerCameraState) => void
}

export const WALL_TO_FACING: Record<WallId, LocationCardinalFacing> = {
  n: "north",
  e: "east",
  s: "south",
  w: "west",
}

export type LocationRoomViewerProps = {
  wallUrls: Partial<Record<LocationCardinalFacing, string | null>>
  wallBusy: Partial<Record<LocationCardinalFacing, boolean>>
  onGenerateWall: (facing: LocationCardinalFacing) => void
  wallGenerateBlockedReason?: (facing: LocationCardinalFacing) => string | null
  /** Stable SPA reconnect id per wall facing. */
  confirmDelayKeyForWall: (facing: LocationCardinalFacing) => string
  wallAboveUrls?: Partial<Record<LocationCardinalFacing, string | null>>
  wallAboveNudgePx?: Partial<Record<LocationCardinalFacing, number>>
  wallAboveBusy?: Partial<Record<LocationCardinalFacing, boolean>>
  onUpperNudgeCommit?: (facing: LocationCardinalFacing, px: number) => void
  /** False: the upper strip cannot be selected or dragged. */
  canNudgeUpper: boolean
  onGenerateWallAbove?: (facing: LocationCardinalFacing) => void
  /** Stable id for the extend button. Countdown is off; the click opens a confirm dialog. */
  confirmDelayKeyForWallAbove?: (facing: LocationCardinalFacing) => string
  panoramaUrl?: string | null
  panoramaBusy?: boolean
  onGeneratePanorama?: () => void
  panoramaGenerateBlockedReason?: string | null
  /** Stable SPA reconnect id for panorama generate/regenerate. */
  confirmDelayKeyForPanorama?: string
  panoramaVersionCount?: number
  onOpenPanoramaVersions?: () => void
  modelLabel?: string | null
  onCreateViewpoint?: (blob: Blob, cameraMeta: RoomViewerCameraState) => void | Promise<void>
  creatingViewpoint?: boolean
  /** Side-panel button calls the same capture; the viewpoints tab calls apply. */
  createViewpointRef?: RefObject<RoomViewerActions | null>
  /** Shot room covers the angle list, so the same grid lives inside the viewer. */
  viewpoints?: LocationViewpointRow[]
  onViewViewpoint?: (viewpoint: LocationViewpointRow, index: number) => void
  /** Busy and the blocked reason for that side-panel button. Null when the viewer unmounts. */
  onCaptureStateChange?: (state: { busy: boolean; blockedReason: string | null } | null) => void
  locationName: string
  variantName?: string | null
  className?: string
}

const FOV_MENU = fovMenuSize(100)
const ROOM_SHAPE_BUTTONS = [
  { id: "cube", labelKey: "locationsPage.roomViewer.shapeCube", icon: Box },
  { id: "cylinder", labelKey: "locationsPage.roomViewer.shapeCylinder", icon: Cylinder },
  { id: "sphere", labelKey: "locationsPage.roomViewer.shapeSphere", icon: Globe },
] as const

function roomToolbarTitle(locationName: string, variantName?: string | null): string {
  const variant = variantName?.trim()
  if (!variant) return locationName
  return `${locationName} · ${variant}`
}

const EMPTY_ANCHORS: Record<WallId, WallScreenAnchor | null> = {
  n: null,
  e: null,
  s: null,
  w: null,
}

function anchorsMoved(
  prev: Record<WallId, WallScreenAnchor | null>,
  next: Record<WallId, WallScreenAnchor | null>,
): boolean {
  for (const id of WALL_IDS) {
    const a = next[id]
    const b = prev[id]
    if ((a == null) !== (b == null)) return true
    if (a && b && (Math.abs(a.x - b.x) > 0.5 || Math.abs(a.y - b.y) > 0.5)) return true
  }
  return false
}

function writeAnchors(
  next: Record<WallId, WallScreenAnchor | null>,
  refs: { current: Partial<Record<WallId, HTMLDivElement | null>> },
) {
  for (const id of WALL_IDS) {
    const el = refs.current[id]
    if (!el) continue
    const anchor = next[id]
    if (!anchor) {
      el.style.display = "none"
      continue
    }
    el.style.display = ""
    el.style.left = `${anchor.x}px`
    el.style.top = `${anchor.y}px`
  }
}

export function LocationRoomViewer({
  wallUrls,
  wallBusy,
  onGenerateWall,
  wallGenerateBlockedReason,
  confirmDelayKeyForWall,
  wallAboveUrls = {},
  wallAboveNudgePx = {},
  wallAboveBusy = {},
  onUpperNudgeCommit,
  canNudgeUpper,
  onGenerateWallAbove,
  confirmDelayKeyForWallAbove,
  panoramaUrl = null,
  panoramaBusy = false,
  onGeneratePanorama,
  panoramaGenerateBlockedReason = null,
  confirmDelayKeyForPanorama,
  panoramaVersionCount = 0,
  onOpenPanoramaVersions,
  modelLabel,
  onCreateViewpoint,
  creatingViewpoint = false,
  createViewpointRef,
  viewpoints,
  onViewViewpoint,
  onCaptureStateChange,
  locationName,
  variantName,
  className,
}: LocationRoomViewerProps) {
  const { t } = useTranslation(["workspace", "shot"])
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const minimapRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<RoomViewerEngine | null>(null)
  const anchorsRef = useRef(EMPTY_ANCHORS)
  const seamAnchorsRef = useRef(EMPTY_ANCHORS)
  const cornerAnchorsRef = useRef(EMPTY_ANCHORS)
  const upperCornerAnchorsRef = useRef(EMPTY_ANCHORS)
  const wallOverlayRefs = useRef<Partial<Record<WallId, HTMLDivElement | null>>>({})
  const wallRegenRefs = useRef<Partial<Record<WallId, HTMLDivElement | null>>>({})
  const wallAboveOverlayRefs = useRef<Partial<Record<WallId, HTMLDivElement | null>>>({})
  const wallAboveRegenRefs = useRef<Partial<Record<WallId, HTMLDivElement | null>>>({})
  const onUpperNudgeCommitRef = useRef(onUpperNudgeCommit)
  onUpperNudgeCommitRef.current = onUpperNudgeCommit

  const [shape, setShape] = useState<RoomShape>("cube")
  const [fov, setFov] = useState(60)
  const [appliedViewpointId, setAppliedViewpointId] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const [flashKey, setFlashKey] = useState(0)
  const [regenTarget, setRegenTarget] = useState<
    | { kind: "panorama" }
    | { kind: "wall"; id: WallId }
    | { kind: "above"; id: WallId }
    | { kind: "extend"; id: WallId }
    | null
  >(null)
  const toolbarTitle = roomToolbarTitle(locationName, variantName)

  const wallLabels = useMemo(
    () =>
      ({
        n: t("locationsPage.facing.north"),
        e: t("locationsPage.facing.east"),
        s: t("locationsPage.facing.south"),
        w: t("locationsPage.facing.west"),
      }) satisfies Record<WallId, string>,
    [t],
  )

  const moveKeyHints = useMemo(
    () => ({
      KeyW: t("locationsPage.scene3d.overlay.keyW"),
      KeyS: t("locationsPage.scene3d.overlay.keyS"),
      KeyA: t("locationsPage.scene3d.overlay.keyA"),
      KeyD: t("locationsPage.scene3d.overlay.keyD"),
      KeyR: t("locationsPage.scene3d.overlay.keyR"),
      KeyF: t("locationsPage.scene3d.overlay.keyF"),
    }),
    [t],
  )

  const hasViewMedia =
    shape === "sphere"
      ? Boolean(panoramaUrl)
      : Boolean(wallUrls.north || wallUrls.east || wallUrls.south || wallUrls.west)

  const createViewpointBlockedReason = !hasViewMedia
    ? t("locationsPage.roomViewer.createViewpointNeedImages")
    : undefined

  useEffect(() => {
    const stage = stageRef.current
    const canvas = canvasRef.current
    const minimap = minimapRef.current
    if (!stage || !canvas || !minimap) return

    const engine = createRoomViewerEngine({
      canvas,
      stage,
      minimap,
      onFovChange: (v) => {
        const next = Math.round(v)
        setFov((prev) => (prev === next ? prev : next))
      },
      // Wall overlays: position via DOM refs (~60fps), not React state.
      onWallAnchorsUpdate: (next) => {
        if (!anchorsMoved(anchorsRef.current, next)) return
        anchorsRef.current = next
        writeAnchors(next, wallOverlayRefs)
      },
      onWallSeamAnchorsUpdate: (next) => {
        if (!anchorsMoved(seamAnchorsRef.current, next)) return
        seamAnchorsRef.current = next
        writeAnchors(next, wallAboveOverlayRefs)
      },
      onWallCornerAnchorsUpdate: (next) => {
        if (!anchorsMoved(cornerAnchorsRef.current, next)) return
        cornerAnchorsRef.current = next
        writeAnchors(next, wallRegenRefs)
      },
      onWallUpperCornerAnchorsUpdate: (next) => {
        if (!anchorsMoved(upperCornerAnchorsRef.current, next)) return
        upperCornerAnchorsRef.current = next
        writeAnchors(next, wallAboveRegenRefs)
      },
      onUpperNudgeCommit: (wall, px) => {
        onUpperNudgeCommitRef.current?.(WALL_TO_FACING[wall], px)
      },
    })
    engineRef.current = engine
    canvas.focus({ preventScroll: true })

    const onPointerDown = () => setDragging(true)
    const onPointerUp = () => setDragging(false)
    canvas.addEventListener("pointerdown", onPointerDown)
    canvas.addEventListener("pointerup", onPointerUp)
    canvas.addEventListener("pointercancel", onPointerUp)

    engine.setUpperNudgeEnabled(canNudgeUpper)

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      engine.destroy()
      engineRef.current = null
    }
    // Engine is created once. Later permission changes go through setUpperNudgeEnabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only scene
  }, [])

  useEffect(() => {
    engineRef.current?.setUpperNudgeEnabled(canNudgeUpper)
  }, [canNudgeUpper])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    const extended = WALL_IDS.some((id) => Boolean(wallAboveUrls[WALL_TO_FACING[id]]))
    engine.setExtended(extended)
    for (const id of WALL_IDS) {
      const facing = WALL_TO_FACING[id]
      void engine.setWallImage(id, wallUrls[facing] ?? null)
      void engine.setUpperWallImage(id, wallAboveUrls[facing] ?? null)
    }
    // Depend on stable per-facing URL strings — parent often rebuilds URL objects
    // on every Inertia jobs reload even when the URLs themselves are unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: compare URL values, not object identity
  }, [
    wallUrls.north,
    wallUrls.east,
    wallUrls.south,
    wallUrls.west,
    wallAboveUrls.north,
    wallAboveUrls.east,
    wallAboveUrls.south,
    wallAboveUrls.west,
  ])

  const wallAboveNudgeNorth = wallAboveNudgePx.north
  const wallAboveNudgeEast = wallAboveNudgePx.east
  const wallAboveNudgeSouth = wallAboveNudgePx.south
  const wallAboveNudgeWest = wallAboveNudgePx.west
  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    const nudgePx = {
      north: wallAboveNudgeNorth,
      east: wallAboveNudgeEast,
      south: wallAboveNudgeSouth,
      west: wallAboveNudgeWest,
    }
    for (const id of WALL_IDS) {
      engine.setUpperNudge(id, nudgePx[WALL_TO_FACING[id]] ?? 0)
    }
  }, [wallAboveNudgeNorth, wallAboveNudgeEast, wallAboveNudgeSouth, wallAboveNudgeWest])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    void engine.setSpherePanorama(panoramaUrl)
  }, [panoramaUrl])

  const handleShape = (next: RoomShape) => {
    setShape(next)
    engineRef.current?.setShape(next)
  }

  const handleFov = (next: number) => {
    setFov(next)
    engineRef.current?.setFov(next)
  }

  const handleCreateViewpoint = useCallback(async () => {
    const engine = engineRef.current
    if (!engine || !onCreateViewpoint || creatingViewpoint || !hasViewMedia) return
    setFlashKey((key) => key + 1)
    const cameraMeta = engine.getCameraState()
    const blob = await engine.captureFrame()
    await onCreateViewpoint(blob, cameraMeta)
  }, [creatingViewpoint, hasViewMedia, onCreateViewpoint])

  const handleApplyViewpoint = useCallback((cameraMeta: RoomViewerCameraState) => {
    const engine = engineRef.current
    if (!engine) return
    engine.applyCameraState(cameraMeta)
    setShape(cameraMeta.shape)
  }, [])

  useEffect(() => {
    if (!createViewpointRef) return
    createViewpointRef.current = {
      createViewpoint: () => {
        void handleCreateViewpoint()
      },
      applyViewpoint: handleApplyViewpoint,
    }
    return () => {
      createViewpointRef.current = null
    }
  }, [createViewpointRef, handleCreateViewpoint, handleApplyViewpoint])

  const onCaptureStateChangeRef = useRef(onCaptureStateChange)
  onCaptureStateChangeRef.current = onCaptureStateChange

  useEffect(() => {
    onCaptureStateChangeRef.current?.({
      busy: creatingViewpoint,
      blockedReason: createViewpointBlockedReason ?? null,
    })
  }, [creatingViewpoint, createViewpointBlockedReason])

  useEffect(() => {
    return () => onCaptureStateChangeRef.current?.(null)
  }, [])

  const onMoveKeyPress = useCallback((code: MoveKeyCode) => {
    engineRef.current?.setHeld(code, true)
  }, [])

  const onMoveKeyRelease = useCallback((code: MoveKeyCode) => {
    engineRef.current?.setHeld(code, false)
  }, [])

  return (
    <div className={cn("relative flex h-full min-h-0 min-w-0 flex-1", className)}>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div ref={stageRef} className="relative min-h-0 w-full flex-1 overflow-hidden bg-black">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-30">
            <div
              className={cn("scene-toolbar pointer-events-auto", TOOLBAR_ROW)}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <span className="block min-w-0 flex-1 truncate text-left text-xs font-medium text-ui-text-primary">
                {toolbarTitle}
              </span>
              <div
                className="flex shrink-0 items-center gap-1.5"
                role="group"
                aria-label={t("locationsPage.roomViewer.shape")}
              >
                {ROOM_SHAPE_BUTTONS.map(({ id, labelKey, icon: Icon }) => (
                  <EditToolbarButton
                    key={id}
                    title={t(labelKey)}
                    hint="tooltip"
                    active={shape === id}
                    onClick={() => handleShape(id)}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </EditToolbarButton>
                ))}
              </div>
              <span className="mx-0.5 h-5 w-px shrink-0 bg-ui-border" aria-hidden />
              <div className="flex shrink-0 items-center gap-1.5">
                <ToolbarSliderButton
                  icon={Aperture}
                  label={t("locationsPage.roomViewer.fov")}
                  valueText={`${Math.round(fov)}°`}
                  min={25}
                  max={100}
                  step={1}
                  value={fov}
                  onValueChange={handleFov}
                  control={<FovControl value={fov} min={25} max={100} onInput={handleFov} onChange={handleFov} />}
                  menuWidthPx={FOV_MENU.width}
                  menuHeightPx={FOV_MENU.height}
                  menuAlign="center"
                />
              </div>
            </div>
          </div>
          <div className="absolute inset-0">
            <canvas
              ref={minimapRef}
              width={150}
              height={150}
              className="pointer-events-none absolute size-0 overflow-hidden opacity-0"
              aria-hidden
            />
            <canvas
              ref={canvasRef}
              tabIndex={0}
              className={cn(
                "absolute inset-0 block h-full w-full outline-none",
                dragging ? "cursor-grabbing" : "cursor-grab",
              )}
              style={{ touchAction: "none" }}
            />
            <div className="pointer-events-none absolute inset-0" aria-hidden>
              <div className="absolute top-1/2 left-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-white/40" />
              <div className="absolute top-1/2 left-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-white/40" />
            </div>
            {(shape === "sphere" ? (["n", "s"] as const) : WALL_IDS).map((id) => {
              const facing = WALL_TO_FACING[id]
              const url = wallUrls[facing] ?? null
              const hasImage = Boolean(url)
              // Sphere: both N/S overlays are one panorama action — shared busy/blocked so they lock/unlock together.
              const busy = shape === "sphere" ? panoramaBusy : Boolean(wallBusy[facing])
              const blockedReason =
                shape === "sphere"
                  ? (panoramaGenerateBlockedReason ?? null)
                  : (wallGenerateBlockedReason?.(facing) ?? null)
              const label =
                shape === "sphere"
                  ? panoramaUrl
                    ? t("locationsPage.roomViewer.regeneratePanorama")
                    : t("locationsPage.roomViewer.generatePanorama")
                  : hasImage
                    ? t("locationsPage.regenerateView", { view: wallLabels[id] })
                    : t("locationsPage.generateView", { view: wallLabels[id] })
              const disabled =
                shape === "sphere" ? !onGeneratePanorama || Boolean(blockedReason) : Boolean(blockedReason)
              const regenerate = shape === "sphere" ? Boolean(panoramaUrl) : hasImage
              if (shape !== "sphere" && regenerate) return null
              return (
                <div
                  key={id}
                  ref={(el) => {
                    wallOverlayRefs.current[id] = el
                    if (!el) return
                    const anchor = anchorsRef.current[id]
                    if (!anchor) {
                      el.style.display = "none"
                      return
                    }
                    el.style.display = ""
                    el.style.left = `${anchor.x}px`
                    el.style.top = `${anchor.y}px`
                  }}
                  className="pointer-events-auto absolute z-20 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: 0, top: 0, display: "none" }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col items-center gap-1">
                    {regenerate ? (
                      <AssetRegenerateOverlayButton
                        title={label}
                        disabled={disabled}
                        disabledReason={blockedReason ?? undefined}
                        busy={busy}
                        onClick={() => setRegenTarget(shape === "sphere" ? { kind: "panorama" } : { kind: "wall", id })}
                      />
                    ) : (
                      <GenerateWithModelLabel
                        label={label}
                        busyLabel={t("generating", { ns: "common" })}
                        modelLabel={modelLabel}
                        busy={busy}
                        disabled={disabled}
                        stopPropagation
                        buttonClassName="shadow-lg"
                        confirmDelayKey={
                          shape === "sphere"
                            ? (confirmDelayKeyForPanorama ?? confirmDelayKeyForWall(facing))
                            : confirmDelayKeyForWall(facing)
                        }
                        onClick={() => {
                          if (shape === "sphere") {
                            onGeneratePanorama?.()
                            return
                          }
                          onGenerateWall(facing)
                        }}
                      />
                    )}
                    {blockedReason && !regenerate ? (
                      <p className="max-w-[14rem] text-center text-[10px] leading-snug text-ui-text-muted drop-shadow-sm">
                        {blockedReason}
                      </p>
                    ) : null}
                  </div>
                </div>
              )
            })}
            {shape !== "sphere"
              ? WALL_IDS.map((id) => {
                  const facing = WALL_TO_FACING[id]
                  if (!wallUrls[facing]) return null
                  const blockedReason = wallGenerateBlockedReason?.(facing) ?? null
                  const disabled = Boolean(blockedReason)
                  const busy = Boolean(wallBusy[facing])
                  return (
                    <div
                      key={`${id}-regen`}
                      ref={(el) => {
                        wallRegenRefs.current[id] = el
                        if (!el) return
                        const anchor = cornerAnchorsRef.current[id]
                        if (!anchor) {
                          el.style.display = "none"
                          return
                        }
                        el.style.display = ""
                        el.style.left = `${anchor.x}px`
                        el.style.top = `${anchor.y}px`
                      }}
                      className="pointer-events-auto absolute z-20 -translate-x-full"
                      style={{ left: 0, top: 0, display: "none" }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <AssetRegenerateOverlayButton
                        title={t("locationsPage.regenerateView", { view: wallLabels[id] })}
                        disabled={disabled}
                        disabledReason={blockedReason ?? undefined}
                        busy={busy}
                        onClick={() => setRegenTarget({ kind: "wall", id })}
                      />
                    </div>
                  )
                })
              : null}
            {shape !== "sphere" && onGenerateWallAbove && confirmDelayKeyForWallAbove
              ? WALL_IDS.map((id) => {
                  const facing = WALL_TO_FACING[id]
                  if (!wallUrls[facing] || wallAboveUrls[facing]) return null
                  const label = t("locationsPage.extendAbove", { view: wallLabels[id] })
                  const busy = Boolean(wallAboveBusy[facing])
                  return (
                    <div
                      key={`${id}-above`}
                      ref={(el) => {
                        wallAboveOverlayRefs.current[id] = el
                        if (!el) return
                        const anchor = seamAnchorsRef.current[id]
                        if (!anchor) {
                          el.style.display = "none"
                          return
                        }
                        el.style.display = ""
                        el.style.left = `${anchor.x}px`
                        el.style.top = `${anchor.y}px`
                      }}
                      className="pointer-events-auto absolute z-20 -translate-x-1/2 -translate-y-4"
                      style={{ left: 0, top: 0, display: "none" }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <GenerateWithModelLabel
                        label={label}
                        busyLabel={t("generating", { ns: "common" })}
                        modelLabel={modelLabel}
                        busy={busy}
                        stopPropagation
                        buttonClassName="shadow-lg"
                        leadingIcon={ArrowUp}
                        confirmDelayKey={confirmDelayKeyForWallAbove(facing)}
                        confirmDelaySeconds={0}
                        onClick={() => setRegenTarget({ kind: "extend", id })}
                      />
                    </div>
                  )
                })
              : null}
            {shape !== "sphere" && onGenerateWallAbove
              ? WALL_IDS.map((id) => {
                  const facing = WALL_TO_FACING[id]
                  if (!wallAboveUrls[facing]) return null
                  const busy = Boolean(wallAboveBusy[facing])
                  return (
                    <div
                      key={`${id}-above-regen`}
                      ref={(el) => {
                        wallAboveRegenRefs.current[id] = el
                        if (!el) return
                        const anchor = upperCornerAnchorsRef.current[id]
                        if (!anchor) {
                          el.style.display = "none"
                          return
                        }
                        el.style.display = ""
                        el.style.left = `${anchor.x}px`
                        el.style.top = `${anchor.y}px`
                      }}
                      className="pointer-events-auto absolute z-20 -translate-x-full"
                      style={{ left: 0, top: 0, display: "none" }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <AssetRegenerateOverlayButton
                        title={t("locationsPage.redrawAbove")}
                        busy={busy}
                        onClick={() => setRegenTarget({ kind: "above", id })}
                      />
                    </div>
                  )
                })
              : null}
          </div>

          {shape === "sphere" && onOpenPanoramaVersions ? (
            <div className="absolute top-12 left-3 z-30" onPointerDown={(e) => e.stopPropagation()}>
              <AssetVersionsBadge
                count={panoramaVersionCount}
                onOpen={onOpenPanoramaVersions}
                title={t("locationsPage.versions")}
                className="shadow-lg"
              />
            </div>
          ) : null}

          {shape !== "sphere" ? (
            <div className="pointer-events-none absolute bottom-4 left-4 z-30">
              <div className="pointer-events-auto">
                <MoveKeys skin="dark" hints={moveKeyHints} onPress={onMoveKeyPress} onRelease={onMoveKeyRelease} />
              </div>
            </div>
          ) : null}

          <div className="pointer-events-none absolute right-4 bottom-4 z-30">
            <div
              className="scene-controls pointer-events-auto"
              data-skin="dark"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <ActionHintTooltip label={createViewpointBlockedReason}>
                <button
                  type="button"
                  className={captureControlButtonClass}
                  aria-label={
                    creatingViewpoint
                      ? t("locationsPage.roomViewer.creatingViewpoint")
                      : t("locationsPage.roomViewer.createViewpoint")
                  }
                  title={createViewpointBlockedReason ? undefined : t("locationsPage.roomViewer.createViewpoint")}
                  disabled={!onCreateViewpoint || creatingViewpoint || !hasViewMedia}
                  onClick={() => void handleCreateViewpoint()}
                >
                  <SceneControlIcon name="camera" size={24} strokeWidth={1} />
                </button>
              </ActionHintTooltip>
            </div>
          </div>
          {flashKey > 0 ? (
            <div key={flashKey} className="scene-control-flash absolute inset-0 z-30" aria-hidden />
          ) : null}
        </div>
        <ConfirmActionDialog
          open={regenTarget != null}
          onOpenChange={(open) => {
            if (!open) setRegenTarget(null)
          }}
          title={
            regenTarget?.kind === "extend"
              ? t("locationsPage.roomViewer.extendConfirmTitle", { view: wallLabels[regenTarget.id] })
              : regenTarget?.kind === "above"
                ? t("locationsPage.roomViewer.regenerateAboveConfirmTitle", {
                    view: wallLabels[regenTarget.id],
                  })
                : t("locationsPage.roomViewer.regenerateConfirmTitle", {
                    view:
                      regenTarget?.kind === "panorama"
                        ? t("locationsPage.views.panorama")
                        : regenTarget
                          ? wallLabels[regenTarget.id]
                          : "",
                  })
          }
          message={
            regenTarget?.kind === "extend"
              ? t("locationsPage.roomViewer.extendConfirmMessage")
              : t("locationsPage.roomViewer.regenerateConfirmMessage")
          }
          confirmLabel={
            regenTarget?.kind === "extend"
              ? t("locationsPage.roomViewer.extendConfirmAction")
              : t("locationsPage.roomViewer.regenerateConfirmAction")
          }
          onConfirm={() => {
            if (!regenTarget) return
            if (regenTarget.kind === "panorama") {
              onGeneratePanorama?.()
              return
            }
            if (regenTarget.kind === "extend" || regenTarget.kind === "above") {
              onGenerateWallAbove?.(WALL_TO_FACING[regenTarget.id])
              return
            }
            onGenerateWall(WALL_TO_FACING[regenTarget.id])
          }}
        />
      </div>
      {viewpoints ? (
        <div
          className="w-52 shrink-0 overflow-y-auto border-l border-ui-border bg-ui-bg p-2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ViewpointThumbGrid
            viewpoints={viewpoints}
            highlightId={appliedViewpointId}
            emptyLabel={t("scenes3dPage.media.viewpointsEmpty")}
            labelFor={(viewpoint, index) =>
              viewpoint.name?.trim() || t("scenes3dPage.viewpoints.itemIndexed", { index: index + 1 })
            }
            applyTitle={t("locationsPage.roomViewer.applyViewpoint")}
            onOpen={(viewpoint) => {
              handleApplyViewpoint(viewpoint.camera_meta)
              setAppliedViewpointId(viewpoint.id)
            }}
            onView={onViewViewpoint}
            viewTitleFor={(viewpoint, index) => {
              const name = viewpoint.name?.trim() || t("scenes3dPage.viewpoints.itemIndexed", { index: index + 1 })
              return t("location.openImage", { ns: "shot", name })
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
